import React, { useState } from 'react';
import {
  Card, CardContent, Box, Typography, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
  Paper, IconButton, Chip, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Checkbox, Radio, RadioGroup, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Alert, Tooltip
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Close as CloseIcon,
  GetApp as GetAppIcon, Visibility as VisibilityIcon, Campaign as CampaignIcon, InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';

const TIPO_DESTINATARIOS = [
  { value: 'todos', label: 'Todos' },
  { value: 'propietarios', label: 'Solo propietarios' },
  { value: 'un_voto_por_casa', label: 'Un voto por casa' },
  { value: 'propietario_principal', label: 'Solo propietario principal' }
];

const EncuestasTab = ({
  encuestas,
  loading,
  error,
  onRefresh,
  onCreate,
  onVerResultados,
  onExportar,
  onCerrar,
  onEliminar,
  fetchWithAuth,
  API_BASE_URL
}) => {
  const [showModalNueva, setShowModalNueva] = useState(false);
  const [showModalResultados, setShowModalResultados] = useState(false);
  const [encuestaResultados, setEncuestaResultados] = useState(null);
  const [loadingResultados, setLoadingResultados] = useState(false);
  const [form, setForm] = useState({
    titulo: '',
    descripcion: '',
    tipo_destinatarios: 'todos',
    fecha_limite: '',
    anonima: true,
    incluir_bloqueados: false,
    mensaje_push: '',
    preguntas: [{ orden: 1, texto_pregunta: '', tipo_pregunta: 'opcion_multiple', obligatoria: true, permite_varias_opciones: false, opcion_1: '', opcion_2: '', opcion_3: '' }]
  });
  const [creando, setCreando] = useState(false);
  const [errorForm, setErrorForm] = useState('');
  const [recordatorioEnviandoId, setRecordatorioEnviandoId] = useState(null);

  const handleEnviarRecordatorio = async (encuesta) => {
    setRecordatorioEnviandoId(encuesta.id);
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/encuestas/${encuesta.id}/enviar-recordatorio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Error al enviar recordatorio');
      }
      const data = await res.json();
      if (onRefresh) onRefresh();
      alert(`Recordatorio enviado a ${data.destinatarios || 0} destinatario(s).`);
    } catch (e) {
      alert(e.message || 'Error al enviar recordatorio');
    } finally {
      setRecordatorioEnviandoId(null);
    }
  };

  const handleAbrirResultados = async (encuesta) => {
    setLoadingResultados(true);
    setEncuestaResultados(null);
    setShowModalResultados(true);
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/encuestas/${encuesta.id}/resultados`);
      if (!res.ok) throw new Error('Error al cargar resultados');
      const data = await res.json();
      setEncuestaResultados(data);
    } catch (e) {
      setEncuestaResultados({ error: e.message || 'Error al cargar' });
    } finally {
      setLoadingResultados(false);
    }
  };

  const handleExportar = async (encuesta) => {
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/encuestas/${encuesta.id}/resultados/export?format=csv`);
      if (!res.ok) throw new Error('Error al exportar');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `encuesta-${encuesta.id}-resultados.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (onExportar) onExportar(encuesta);
    }
  };

  const agregarPregunta = () => {
    if (form.preguntas.length >= 10) return;
    setForm(f => ({
      ...f,
      preguntas: [...f.preguntas, { orden: f.preguntas.length + 1, texto_pregunta: '', tipo_pregunta: 'opcion_multiple', obligatoria: true, permite_varias_opciones: false, opcion_1: '', opcion_2: '', opcion_3: '' }]
    }));
  };

  const actualizarPregunta = (index, field, value) => {
    setForm(f => ({
      ...f,
      preguntas: f.preguntas.map((p, i) => i === index ? { ...p, [field]: value } : p)
    }));
  };

  const quitarPregunta = (index) => {
    if (form.preguntas.length <= 1) return;
    setForm(f => ({
      ...f,
      preguntas: f.preguntas.filter((_, i) => i !== index).map((p, i) => ({ ...p, orden: i + 1 }))
    }));
  };

  const handleSubmitNueva = async () => {
    setErrorForm('');
    if (!form.titulo.trim()) { setErrorForm('El título es requerido'); return; }
    const preguntas = form.preguntas.map((p, i) => ({
      orden: i + 1,
      texto_pregunta: p.texto_pregunta.trim(),
      tipo_pregunta: p.tipo_pregunta,
      obligatoria: p.obligatoria,
      permite_varias_opciones: p.permite_varias_opciones,
      opcion_1: p.tipo_pregunta === 'opcion_multiple' ? (p.opcion_1 || '').trim() || null : null,
      opcion_2: p.tipo_pregunta === 'opcion_multiple' ? (p.opcion_2 || '').trim() || null : null,
      opcion_3: p.tipo_pregunta === 'opcion_multiple' ? (p.opcion_3 || '').trim() || null : null
    }));
    const invalid = preguntas.find(p => !p.texto_pregunta);
    if (invalid) { setErrorForm('Todas las preguntas deben tener texto'); return; }
    const multiSinOpcion = preguntas.find(p => p.tipo_pregunta === 'opcion_multiple' && !p.opcion_1 && !p.opcion_2 && !p.opcion_3);
    if (multiSinOpcion) { setErrorForm('Cada pregunta de opción múltiple debe tener al menos una opción'); return; }
    setCreando(true);
    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/encuestas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          titulo: form.titulo.trim(),
          descripcion: form.descripcion.trim() || null,
          tipo_destinatarios: form.tipo_destinatarios,
          fecha_limite: form.fecha_limite || null,
          anonima: form.anonima,
          incluir_bloqueados: form.incluir_bloqueados,
          mensaje_push: form.mensaje_push.trim() || null,
          preguntas
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Error al crear encuesta');
      }
      setShowModalNueva(false);
      setForm({ titulo: '', descripcion: '', tipo_destinatarios: 'todos', fecha_limite: '', anonima: true, incluir_bloqueados: false, mensaje_push: '', preguntas: [{ orden: 1, texto_pregunta: '', tipo_pregunta: 'opcion_multiple', obligatoria: true, permite_varias_opciones: false, opcion_1: '', opcion_2: '', opcion_3: '' }] });
      onRefresh && onRefresh();
    } catch (e) {
      setErrorForm(e.message || 'Error al crear');
    } finally {
      setCreando(false);
    }
  };

  const formatFecha = (f) => {
    if (!f) return '—';
    const d = new Date(f);
    return d.toLocaleDateString('es-MX', { dateStyle: 'short' });
  };

  const labelTipo = (t) => TIPO_DESTINATARIOS.find(x => x.value === t)?.label || t;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
        <Tooltip title="Crea encuestas para los residentes y revisa los resultados.">
          <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon fontSize="small" color="action" /></IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => {}}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={6} md={3}>
          <Card variant="outlined"><CardContent><Typography color="text.secondary">Total</Typography><Typography variant="h5">{encuestas.length}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined"><CardContent><Typography color="text.secondary">Activas</Typography><Typography variant="h5" color="success.main">{encuestas.filter(e => e.activa).length}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setErrorForm(''); setShowModalNueva(true); }}>
            Nueva encuesta
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: '55vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Quién responde</TableCell>
                <TableCell>Fecha límite</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Respuestas</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {encuestas.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell>{e.titulo}</TableCell>
                  <TableCell>{labelTipo(e.tipo_destinatarios)}</TableCell>
                  <TableCell>{formatFecha(e.fecha_limite)}</TableCell>
                  <TableCell>
                    <Chip label={e.activa ? 'Activa' : 'Cerrada'} color={e.activa ? 'success' : 'default'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{e.total_envios ?? 0}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => handleAbrirResultados(e)} title="Ver resultados"><VisibilityIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleExportar(e)} title="Exportar CSV"><GetAppIcon /></IconButton>
                    {e.activa && (!e.fecha_limite || new Date(e.fecha_limite) > new Date()) && (
                      <IconButton size="small" onClick={() => handleEnviarRecordatorio(e)} disabled={recordatorioEnviandoId === e.id} title="Enviar recordatorio push a quienes no han respondido"><CampaignIcon /></IconButton>
                    )}
                    {e.activa && e.total_envios === 0 && (
                      <IconButton size="small" onClick={() => onCerrar && onCerrar(e.id)} title="Cerrar"> <CloseIcon /></IconButton>
                    )}
                    <IconButton size="small" color="error" onClick={() => onEliminar && onEliminar(e.id)} title="Eliminar"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {encuestas.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>No hay encuestas. Crea una con el botón &quot;Nueva encuesta&quot;.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal Nueva encuesta */}
      <Dialog open={showModalNueva} onClose={() => !creando && setShowModalNueva(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nueva encuesta</DialogTitle>
        <DialogContent>
          {errorForm && <Alert severity="error" sx={{ mb: 2 }}>{errorForm}</Alert>}
          <TextField fullWidth label="Título" value={form.titulo} onChange={(e) => setForm(f => ({ ...f, titulo: e.target.value }))} margin="normal" required />
          <TextField fullWidth label="Descripción (opcional)" value={form.descripcion} onChange={(e) => setForm(f => ({ ...f, descripcion: e.target.value }))} margin="normal" multiline rows={2} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Quién puede responder</InputLabel>
            <Select value={form.tipo_destinatarios} label="Quién puede responder" onChange={(e) => setForm(f => ({ ...f, tipo_destinatarios: e.target.value }))}>
              {TIPO_DESTINATARIOS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth type="datetime-local" label="Fecha límite (opcional)" value={form.fecha_limite} onChange={(e) => setForm(f => ({ ...f, fecha_limite: e.target.value }))} margin="normal" InputLabelProps={{ shrink: true }} />
          <FormControlLabel control={<Checkbox checked={form.anonima} onChange={(e) => setForm(f => ({ ...f, anonima: e.target.checked }))} />} label="Encuesta anónima (solo conteos en resultados)" />
          <FormControlLabel control={<Checkbox checked={form.incluir_bloqueados} onChange={(e) => setForm(f => ({ ...f, incluir_bloqueados: e.target.checked }))} />} label="Incluir usuarios bloqueados" />
          <TextField fullWidth label="Mensaje para notificación push (opcional)" value={form.mensaje_push} onChange={(e) => setForm(f => ({ ...f, mensaje_push: e.target.value }))} margin="normal" placeholder="Texto que verán los residentes en la notificación; si se deja vacío se usará el título de la encuesta" helperText="Se envía push a los destinatarios al publicar" />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Preguntas (1 a 10)</Typography>
          {form.preguntas.map((p, index) => (
            <Card key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">Pregunta {index + 1}</Typography>
                {form.preguntas.length > 1 && <Button size="small" color="error" onClick={() => quitarPregunta(index)}>Quitar</Button>}
              </Box>
              <TextField fullWidth size="small" label="Texto de la pregunta" value={p.texto_pregunta} onChange={(e) => actualizarPregunta(index, 'texto_pregunta', e.target.value)} margin="dense" required />
              <FormControl component="fieldset" margin="dense">
                <RadioGroup row value={p.tipo_pregunta} onChange={(e) => actualizarPregunta(index, 'tipo_pregunta', e.target.value)}>
                  <FormControlLabel value="opcion_multiple" control={<Radio size="small" />} label="Opción múltiple" />
                  <FormControlLabel value="texto_abierto" control={<Radio size="small" />} label="Texto abierto" />
                </RadioGroup>
              </FormControl>
              {p.tipo_pregunta === 'opcion_multiple' && (
                <>
                  <FormControlLabel control={<Checkbox size="small" checked={p.permite_varias_opciones} onChange={(e) => actualizarPregunta(index, 'permite_varias_opciones', e.target.checked)} />} label="Permitir varias opciones (checkbox)" />
                  <TextField fullWidth size="small" label="Opción 1" value={p.opcion_1} onChange={(e) => actualizarPregunta(index, 'opcion_1', e.target.value)} margin="dense" />
                  <TextField fullWidth size="small" label="Opción 2" value={p.opcion_2} onChange={(e) => actualizarPregunta(index, 'opcion_2', e.target.value)} margin="dense" />
                  <TextField fullWidth size="small" label="Opción 3" value={p.opcion_3} onChange={(e) => actualizarPregunta(index, 'opcion_3', e.target.value)} margin="dense" />
                </>
              )}
              <FormControlLabel control={<Checkbox size="small" checked={p.obligatoria} onChange={(e) => actualizarPregunta(index, 'obligatoria', e.target.checked)} />} label="Obligatoria" />
            </Card>
          ))}
          {form.preguntas.length < 10 && <Button startIcon={<AddIcon />} onClick={agregarPregunta}>Agregar pregunta</Button>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !creando && setShowModalNueva(false)} disabled={creando}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmitNueva} disabled={creando}>{creando ? 'Creando...' : 'Crear encuesta'}</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Resultados */}
      <Dialog open={showModalResultados} onClose={() => setShowModalResultados(false)} maxWidth="md" fullWidth>
        <DialogTitle>Resultados de la encuesta</DialogTitle>
        <DialogContent>
          {loadingResultados && <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>}
          {encuestaResultados?.error && <Alert severity="error">{encuestaResultados.error}</Alert>}
          {encuestaResultados && !encuestaResultados.error && (
            <>
              <Typography variant="body1" gutterBottom><strong>{encuestaResultados.encuesta?.titulo}</strong></Typography>
              <Typography variant="body2" color="text.secondary">Total de respuestas: {encuestaResultados.total_envios}</Typography>
              {encuestaResultados.resultados?.map((r, i) => (
                <Card key={i} variant="outlined" sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>{r.texto_pregunta}</Typography>
                  {r.tipo_pregunta === 'opcion_multiple' && (
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {[r.opcion_1, r.opcion_2, r.opcion_3].filter(Boolean).map((op, j) => (
                        <li key={j}><Typography variant="body2">{op}: {r.conteos?.[op] ?? 0}</Typography></li>
                      ))}
                    </Box>
                  )}
                  {r.tipo_pregunta === 'texto_abierto' && (
                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {r.respuestas_abiertas?.length ? r.respuestas_abiertas.map((resp, j) => <Typography key={j} variant="body2" sx={{ py: 0.5 }}>• {resp.valor}</Typography>) : <Typography variant="body2" color="text.secondary">Sin respuestas</Typography>}
                    </Box>
                  )}
                </Card>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {encuestaResultados && !encuestaResultados.error && encuestaResultados.encuesta && (
            <Button startIcon={<GetAppIcon />} onClick={() => { const e = { id: encuestaResultados.encuesta.id }; handleExportar(e); }}>Exportar CSV</Button>
          )}
          <Button onClick={() => setShowModalResultados(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(EncuestasTab);
